'use client';

import { memo, useCallback } from 'react';

import { CheckIcon, SettingsIcon } from '@air/design-icons';
import { Popover } from '@air/primitive-popover';
import { cn } from '@air/utils-tailwind';

import { LIST_VIEW_COLUMNS, type ListViewColumnField } from '~/constants/listViewColumns';

export type ColumnsPopoverProps = {
  onToggleColumn: (field: ListViewColumnField) => void;
  visibleColumns: ListViewColumnField[];
};

export type ColumnOptionRowProps = {
  checked: boolean;
  field: ListViewColumnField;
  label: string;
  onToggleColumn: (field: ListViewColumnField) => void;
};

const ColumnOptionRow = memo(({ checked, field, label, onToggleColumn }: ColumnOptionRowProps) => {
  const handleClick = useCallback(() => {
    onToggleColumn(field);
  }, [field, onToggleColumn]);

  return (
    <button
      aria-pressed={checked}
      className="flex w-full items-center justify-between rounded-6 px-2 py-1.5 text-11 text-neutral-12 hover:bg-neutral-3"
      onClick={handleClick}
      type="button"
    >
      <span>{label}</span>
      <span
        className={cn(
          'flex size-4 items-center justify-center rounded-4 border transition-colors',
          checked ? 'border-brand-9 bg-brand-9' : 'border-neutral-6 bg-neutral-1',
        )}
      >
        {checked && <CheckIcon aria-hidden="true" className="text-white" size="xs" />}
      </span>
    </button>
  );
});

ColumnOptionRow.displayName = 'ColumnOptionRow';

export const ColumnsPopover = memo(({ onToggleColumn, visibleColumns }: ColumnsPopoverProps) => {
  return (
    <Popover
      align="end"
      aria-label="Choose columns"
      className="w-44 rounded-8 bg-neutral-2 p-1 shadow-popover"
      sideOffset={4}
      trigger={
        <button
          aria-label="Choose columns"
          className="flex shrink-0 items-center gap-1 rounded-6 border border-neutral-5 bg-neutral-2 px-1.5 py-1 font-medium text-10 text-neutral-10 transition-colors hover:border-neutral-6 hover:text-neutral-12"
          data-testid="list-view-columns-button"
          type="button"
        >
          <SettingsIcon aria-hidden="true" size="xs" />
          Columns
        </button>
      }
    >
      {LIST_VIEW_COLUMNS.map(({ field, label }) => (
        <ColumnOptionRow
          checked={visibleColumns.includes(field)}
          field={field}
          key={field}
          label={label}
          onToggleColumn={onToggleColumn}
        />
      ))}
    </Popover>
  );
});

ColumnsPopover.displayName = 'ColumnsPopover';
